import z from 'zod';
/** Initial userLicensesLimit when SSO is first enabled (INV-SSO-CFG-7). */
export const SSO_DEFAULT_USER_LICENSES_LIMIT = 100;
const FIRM_SLUG_PATTERN = /^[\da-z]+(?:-[\da-z]+)*$/;
export const firmSlugValue = z
    .string()
    .trim()
    .min(1)
    .regex(FIRM_SLUG_PATTERN)
    .brand('FirmSlug');
export const createFirmSlug = (value) => firmSlugValue.parse(value);
export const isFirmSlug = (value) => firmSlugValue.safeParse(value).success;
export const allowedEmailDomainValue = z
    .string()
    .trim()
    .min(1)
    .transform(value => value.toLowerCase().replace(/^@/, ''))
    .refine(value => !value.includes('@') && !/\s/.test(value), { message: 'Invalid email domain' })
    .brand('AllowedEmailDomain');
export const createAllowedEmailDomain = (value) => allowedEmailDomainValue.parse(value);
export const isAllowedEmailDomain = (value) => allowedEmailDomainValue.safeParse(value).success;
export const idpEntityIdValue = z.string().trim().min(1).brand('IdpEntityId');
export const createIdpEntityId = (value) => idpEntityIdValue.parse(value);
export const idpEntryPointValue = z
    .url()
    .brand('IdpEntryPoint');
export const createIdpEntryPoint = (value) => idpEntryPointValue.parse(value);
export const idpX509CertificateValue = z
    .string()
    .trim()
    .min(1)
    .brand('IdpX509Certificate');
export const createIdpX509Certificate = (value) => idpX509CertificateValue.parse(value);
const BASE64_BODY_PATTERN = /^[\d+/A-Za-z]+={0,2}$/;
const PEM_BOUNDARY_PATTERN = /-----(?:BEGIN|END)[^-]*-----/g;
/**
 * Admins paste the IdP certificate by hand, so it arrives in every shape:
 * full PEM, bare base64, one line or wrapped, CRLF endings, or indented
 * straight out of a federation metadata XML. Strip the PEM boundaries and
 * every whitespace character to get back to the raw base64 body.
 */
export const extractIdpCertificateBody = (value) => value
    .replaceAll(PEM_BOUNDARY_PATTERN, '')
    .replaceAll(/\s/g, '');
/**
 * SAML libraries only accept strict PEM or strict base64 and reject anything
 * else — node-saml does so late, during the ACS callback. Check the shape up
 * front so a bad paste is caught when it is saved, not weeks later on a
 * user's first login. Does not verify that the bytes decode to an X.509
 * certificate: that belongs to whoever can use node:crypto.
 */
export const isIdpCertificateBodyValid = (value) => {
    const body = extractIdpCertificateBody(value);
    return body.length > 0 && BASE64_BODY_PATTERN.test(body);
};
export const samlNameIdValue = z.string().trim().min(1).brand('SamlNameId');
export const createSamlNameId = (value) => samlNameIdValue.parse(value);
export const userEmailValue = z
    .email()
    .brand('UserEmail');
export const createUserEmail = (value) => userEmailValue.parse(value);
export const isUserEmail = (value) => userEmailValue.safeParse(value).success;
export const extractEmailDomain = (email) => {
    const atIndex = email.lastIndexOf('@');
    return createAllowedEmailDomain(email.slice(atIndex + 1));
};
export const firmRoleIdValue = z.string().trim().min(1).brand('FirmRoleId');
export const createFirmRoleId = (value) => firmRoleIdValue.parse(value);
export const isSsoConfigComplete = (config) => config.entityId.trim().length > 0
    && config.entryPoint.trim().length > 0
    && config.certificate.trim().length > 0
    && config.allowedEmailDomains.length > 0;
export const createFirmSsoConfig = (input) => ({
    allowedEmailDomains: input.allowedEmailDomains.map(element => createAllowedEmailDomain(element)),
    certificate: createIdpX509Certificate(input.certificate),
    defaultUserRoleId: resolveOptionalFirmRoleId(input.defaultUserRoleId),
    // Entra: do not demand PasswordProtectedTransport (AADSTS75011).
    disableRequestedAuthnContext: input.disableRequestedAuthnContext ?? true,
    entityId: createIdpEntityId(input.entityId),
    entryPoint: createIdpEntryPoint(input.entryPoint),
    isEnabled: input.isEnabled,
    // Signed assertion carries identity; keep required unless IdP cannot.
    wantAssertionsSigned: input.wantAssertionsSigned ?? true,
    // Entra often signs assertion only; requiring Response breaks ACS.
    wantAuthnResponseSigned: input.wantAuthnResponseSigned ?? false,
});
function resolveOptionalFirmRoleId(value) {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    return createFirmRoleId(value);
}
export { createFirmId } from '../../subscription/value-objects/ids';
//# sourceMappingURL=value-objects.js.map